import { useState, useEffect, useCallback } from "react";
import {
  TrendingUp, TrendingDown, Euro, Percent, Package, Clock, AlertTriangle,
  BarChart3, PieChart, ArrowUpRight, ArrowDownRight, Minus, RefreshCw,
  ChevronDown, Filter, Download, Info
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { authFetch } from "../services/authService";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend, Cell, PieChart as RechartsPie, Pie
} from "recharts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Colori per i grafici
const COLORS = {
  primary: "#f97316",
  green: "#22c55e",
  red: "#ef4444",
  blue: "#3b82f6",
  yellow: "#eab308",
  zinc: "#71717a"
};

export default function MarginiProfitto({ user }) {
  const [kpi, setKpi] = useState(null);
  const [trend, setTrend] = useState([]);
  const [byQuote, setByQuote] = useState([]);
  const [byArticle, setByArticle] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState("mese");
  const [activeTab, setActiveTab] = useState("overview");

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [kpiRes, trendRes, quoteRes, articleRes, alertsRes, summaryRes] = await Promise.all([
        authFetch(`${API}/analytics/margins/kpi?periodo=${periodo}`, { }),
        authFetch(`${API}/analytics/margins/trend?periodo=6mesi`, { }),
        authFetch(`${API}/analytics/margins/by-quote?limit=20&order_by=margin_desc`, { }),
        authFetch(`${API}/analytics/margins/by-article?limit=15`, { }),
        authFetch(`${API}/analytics/margins/alerts`, { }),
        authFetch(`${API}/analytics/margins/summary`, { })
      ]);

      if (kpiRes.ok) setKpi(await kpiRes.json());
      if (trendRes.ok) setTrend(await trendRes.json());
      if (quoteRes.ok) setByQuote(await quoteRes.json());
      if (articleRes.ok) setByArticle(await articleRes.json());
      if (alertsRes.ok) setAlerts(await alertsRes.json());
      if (summaryRes.ok) setSummary(await summaryRes.json());

    } catch (error) {
      console.error("Error fetching margin data:", error);
      toast.error("Errore nel caricamento dati margini");
    } finally {
      setLoading(false);
    }
  }, [periodo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Componente KPI Card
  const KPICard = ({ icon: Icon, title, value, subtitle, trend: trendValue, color = "primary", large = false }) => {
    const colorClasses = {
      primary: "text-primary bg-primary/10",
      green: "text-green-500 bg-green-500/10",
      red: "text-red-500 bg-red-500/10",
      blue: "text-blue-500 bg-blue-500/10",
      yellow: "text-yellow-500 bg-yellow-500/10"
    };

    return (
      <div className={`bg-zinc-900 border border-zinc-800 rounded-sm p-4 ${large ? 'col-span-2' : ''}`}>
        <div className="flex items-start justify-between">
          <div className={`p-2 rounded-sm ${colorClasses[color]}`}>
            <Icon className="w-5 h-5" />
          </div>
          {trendValue !== undefined && (
            <div className={`flex items-center text-sm ${trendValue >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {trendValue >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
              <span>{Math.abs(trendValue)}%</span>
            </div>
          )}
        </div>
        <p className="text-zinc-400 text-sm mt-3">{title}</p>
        <p className={`${large ? 'text-3xl' : 'text-2xl'} font-bold text-white mt-1`}>{value}</p>
        {subtitle && <p className="text-xs text-zinc-500 mt-1">{subtitle}</p>}
      </div>
    );
  };

  // Tab Navigation
  const tabs = [
    { id: "overview", label: "Panoramica", icon: PieChart },
    { id: "preventivi", label: "Per Preventivo", icon: BarChart3 },
    { id: "articoli", label: "Per Articolo", icon: Package },
    { id: "alerts", label: "Alert", icon: AlertTriangle, badge: alerts.filter(a => a.severita === "critico").length }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="margini-profitto-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white uppercase font-['Barlow_Condensed'] tracking-wider">
            Analisi Margini di Profitto
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Monitora la redditività della tua officina
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Periodo selector */}
          <select
            value={periodo}
            onChange={(e) => setPeriodo(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-white text-sm"
            data-testid="periodo-select"
          >
            <option value="mese">Ultimo mese</option>
            <option value="trimestre">Ultimo trimestre</option>
            <option value="anno">Ultimo anno</option>
          </select>
          
          <Button onClick={fetchData} variant="outline" className="border-zinc-700" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Aggiorna
          </Button>
        </div>
      </div>

      {/* Summary Cards - Confronto mese corrente vs precedente */}
      {summary && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-zinc-400 uppercase">Confronto Mensile</h3>
            <div className={`flex items-center gap-1 px-2 py-1 rounded text-sm ${
              summary.trend === 'up' ? 'bg-green-500/10 text-green-500' : 
              summary.trend === 'down' ? 'bg-red-500/10 text-red-500' : 
              'bg-zinc-800 text-zinc-400'
            }`}>
              {summary.trend === 'up' ? <TrendingUp className="w-4 h-4" /> : 
               summary.trend === 'down' ? <TrendingDown className="w-4 h-4" /> : 
               <Minus className="w-4 h-4" />}
              <span>{summary.variazione_margine_punti > 0 ? '+' : ''}{summary.variazione_margine_punti} punti</span>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-zinc-500 mb-1">Mese Corrente</p>
              <p className="text-2xl font-bold text-white">
                {summary.mese_corrente.margine_percentuale}%
              </p>
              <p className="text-sm text-zinc-400">
                €{summary.mese_corrente.margine.toLocaleString()} su €{summary.mese_corrente.fatturato.toLocaleString()}
              </p>
            </div>
            <div>
              <p className="text-xs text-zinc-500 mb-1">Mese Precedente</p>
              <p className="text-2xl font-bold text-zinc-400">
                {summary.mese_precedente.margine_percentuale}%
              </p>
              <p className="text-sm text-zinc-500">
                €{summary.mese_precedente.margine.toLocaleString()} su €{summary.mese_precedente.fatturato.toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-1 border-b border-zinc-800 pb-1">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id 
                ? 'text-primary border-b-2 border-primary -mb-[5px]' 
                : 'text-zinc-400 hover:text-white'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
            {tab.badge > 0 && (
              <span className="bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {tab.badge}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "overview" && kpi && (
        <div className="space-y-6">
          {/* KPI Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KPICard
              icon={Euro}
              title="Fatturato Totale"
              value={`€${kpi.fatturato_totale.toLocaleString()}`}
              subtitle={`${kpi.num_preventivi} lavori completati`}
              color="blue"
            />
            <KPICard
              icon={Percent}
              title="Margine Lordo"
              value={`${kpi.margine_lordo_percentuale}%`}
              subtitle={`€${kpi.margine_lordo.toLocaleString()}`}
              color={kpi.margine_lordo_percentuale >= 30 ? "green" : kpi.margine_lordo_percentuale >= 15 ? "yellow" : "red"}
            />
            <KPICard
              icon={Package}
              title="Margine Ricambi"
              value={`${kpi.margine_ricambi_percentuale}%`}
              subtitle={`€${kpi.margine_ricambi.toLocaleString()}`}
              color={kpi.margine_ricambi_percentuale >= 25 ? "green" : "yellow"}
            />
            <KPICard
              icon={Clock}
              title="Margine Manodopera"
              value={`${kpi.margine_manodopera_percentuale}%`}
              subtitle={`€${kpi.margine_manodopera.toLocaleString()}`}
              color="green"
            />
          </div>

          {/* Breakdown Card */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Composizione Margine */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
              <h3 className="text-sm font-medium text-zinc-400 uppercase mb-4">Composizione Margine</h3>
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-zinc-400">Ricavi Ricambi</span>
                    <span className="text-white">€{(kpi.costi_ricambi + kpi.margine_ricambi).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-zinc-500 ml-4">- Costo Acquisto</span>
                    <span className="text-red-400">-€{kpi.costi_ricambi.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium border-t border-zinc-800 pt-1">
                    <span className="text-green-400 ml-4">= Margine Ricambi</span>
                    <span className="text-green-400">€{kpi.margine_ricambi.toLocaleString()}</span>
                  </div>
                </div>
                
                <div className="border-t border-zinc-700 pt-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-zinc-400">Ricavi Manodopera</span>
                    <span className="text-white">€{kpi.ricavi_manodopera.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-zinc-500 ml-4">- Costo Stimato (30%)</span>
                    <span className="text-red-400">-€{(kpi.ricavi_manodopera * 0.30).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-sm font-medium border-t border-zinc-800 pt-1">
                    <span className="text-green-400 ml-4">= Margine Manodopera</span>
                    <span className="text-green-400">€{kpi.margine_manodopera.toLocaleString()}</span>
                  </div>
                </div>

                <div className="border-t border-zinc-700 pt-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-zinc-500">- Costi Fissi Stimati (10%)</span>
                    <span className="text-red-400">-€{kpi.costi_fissi_stimati.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t border-primary pt-2 mt-2">
                    <span className="text-primary">MARGINE LORDO</span>
                    <span className="text-primary">€{kpi.margine_lordo.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Trend Chart */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-4">
              <h3 className="text-sm font-medium text-zinc-400 uppercase mb-4">Trend Margine (6 mesi)</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="periodo" tick={{ fill: '#71717a', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#71717a', fontSize: 11 }} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#18181b', border: '1px solid #27272a' }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="margine_percentuale" 
                    stroke={COLORS.primary} 
                    strokeWidth={2}
                    dot={{ fill: COLORS.primary }}
                    name="Margine %"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Info box */}
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-sm p-4 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-300">
              <p className="font-medium mb-1">Come migliorare i margini?</p>
              <ul className="list-disc ml-4 space-y-1 text-blue-400">
                <li>Inserisci il <strong>costo di acquisto</strong> per ogni ricambio nei preventivi</li>
                <li>Monitora gli articoli con margine basso nella tab "Per Articolo"</li>
                <li>Verifica i preventivi con margine negativo nella tab "Alert"</li>
              </ul>
            </div>
          </div>
        </div>
      )}

      {activeTab === "preventivi" && (
        <div className="space-y-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-zinc-800">
                <tr>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase px-4 py-3">Preventivo</th>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase px-4 py-3">Cliente</th>
                  <th className="text-right text-xs font-medium text-zinc-400 uppercase px-4 py-3">Fatturato</th>
                  <th className="text-right text-xs font-medium text-zinc-400 uppercase px-4 py-3">Costi</th>
                  <th className="text-right text-xs font-medium text-zinc-400 uppercase px-4 py-3">Margine</th>
                  <th className="text-right text-xs font-medium text-zinc-400 uppercase px-4 py-3">%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {byQuote.map((item) => (
                  <tr key={item.quote_id} className="hover:bg-zinc-800/50" data-testid={`quote-row-${item.quote_id}`}>
                    <td className="px-4 py-3">
                      <p className="text-white font-medium">#{item.quote_number}</p>
                      <p className="text-xs text-zinc-500">{item.veicolo}</p>
                    </td>
                    <td className="px-4 py-3 text-zinc-300 text-sm">{item.cliente}</td>
                    <td className="px-4 py-3 text-right text-white">€{item.totale_fatturato.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-zinc-400">€{item.totale_costi.toLocaleString()}</td>
                    <td className={`px-4 py-3 text-right font-medium ${item.margine_totale >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      €{item.margine_totale.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        item.margine_percentuale >= 30 ? 'bg-green-500/20 text-green-400' :
                        item.margine_percentuale >= 15 ? 'bg-yellow-500/20 text-yellow-400' :
                        item.margine_percentuale >= 0 ? 'bg-orange-500/20 text-orange-400' :
                        'bg-red-500/20 text-red-400'
                      }`}>
                        {item.margine_percentuale}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {byQuote.length === 0 && (
              <div className="text-center py-8 text-zinc-500">
                Nessun preventivo con dati margine disponibili
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "articoli" && (
        <div className="space-y-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-zinc-800">
                <tr>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase px-4 py-3">Articolo</th>
                  <th className="text-left text-xs font-medium text-zinc-400 uppercase px-4 py-3">Categoria</th>
                  <th className="text-right text-xs font-medium text-zinc-400 uppercase px-4 py-3">Qty</th>
                  <th className="text-right text-xs font-medium text-zinc-400 uppercase px-4 py-3">Fatturato</th>
                  <th className="text-right text-xs font-medium text-zinc-400 uppercase px-4 py-3">Costo</th>
                  <th className="text-right text-xs font-medium text-zinc-400 uppercase px-4 py-3">Margine</th>
                  <th className="text-right text-xs font-medium text-zinc-400 uppercase px-4 py-3">Ricarico</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800">
                {byArticle.map((item, idx) => (
                  <tr key={idx} className="hover:bg-zinc-800/50" data-testid={`article-row-${idx}`}>
                    <td className="px-4 py-3">
                      <p className="text-white font-medium text-sm">{item.descrizione}</p>
                      {item.codice && <p className="text-xs text-zinc-500">{item.codice}</p>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${
                        item.categoria === 'RICAMBIO' ? 'bg-blue-500/20 text-blue-400' :
                        item.categoria === 'MANODOPERA' ? 'bg-green-500/20 text-green-400' :
                        'bg-zinc-700 text-zinc-300'
                      }`}>
                        {item.categoria}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-zinc-400">{item.quantita_venduta}</td>
                    <td className="px-4 py-3 text-right text-white">€{item.fatturato.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right text-zinc-400">
                      {item.costo > 0 ? `€${item.costo.toLocaleString()}` : '-'}
                    </td>
                    <td className={`px-4 py-3 text-right font-medium ${item.margine >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                      €{item.margine.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {item.costo > 0 ? (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          item.ricarico_percentuale >= 50 ? 'bg-green-500/20 text-green-400' :
                          item.ricarico_percentuale >= 20 ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          +{item.ricarico_percentuale}%
                        </span>
                      ) : (
                        <span className="text-zinc-500 text-xs">N/D</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {byArticle.length === 0 && (
              <div className="text-center py-8 text-zinc-500">
                Nessun articolo con dati disponibili
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === "alerts" && (
        <div className="space-y-4">
          {alerts.length === 0 ? (
            <div className="bg-green-500/10 border border-green-500/30 rounded-sm p-6 text-center">
              <TrendingUp className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-green-400 font-medium">Nessun alert attivo</p>
              <p className="text-green-400/70 text-sm mt-1">Tutti i margini sono nella norma</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert, idx) => (
                <div 
                  key={idx}
                  className={`border rounded-sm p-4 ${
                    alert.severita === 'critico' ? 'bg-red-500/10 border-red-500/30' :
                    alert.severita === 'attenzione' ? 'bg-yellow-500/10 border-yellow-500/30' :
                    'bg-blue-500/10 border-blue-500/30'
                  }`}
                  data-testid={`alert-${idx}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${
                        alert.severita === 'critico' ? 'text-red-400' :
                        alert.severita === 'attenzione' ? 'text-yellow-400' :
                        'text-blue-400'
                      }`} />
                      <div>
                        <p className={`font-medium ${
                          alert.severita === 'critico' ? 'text-red-400' :
                          alert.severita === 'attenzione' ? 'text-yellow-400' :
                          'text-blue-400'
                        }`}>
                          Preventivo #{alert.quote_number}
                        </p>
                        <p className="text-white text-sm mt-1">{alert.messaggio}</p>
                        <p className="text-zinc-400 text-xs mt-1">Cliente: {alert.cliente}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs uppercase font-medium ${
                      alert.severita === 'critico' ? 'bg-red-500/20 text-red-400' :
                      alert.severita === 'attenzione' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-blue-500/20 text-blue-400'
                    }`}>
                      {alert.severita}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
